import React, { useEffect, useMemo, useState } from "react";
import { Button, Select, Spin,  Modal, Upload } from "antd";
import { useQuery } from "react-query";
import useAxiosInstance from "../../lib/useAxiosInstance";
import "./ElectionModeDashboard.css";
import RightPanel, { NotesPanel, MlaAffidavitPanel, NewsPanelAdmin } from "./RightPanel";
import { AlignLeft, AlignRight, StickyNote, FileSpreadsheet, Newspaper } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Tooltip } from "antd";
import { Upload as UploadIcon } from "lucide-react";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatIndianNumber = (num) => Number(num || 0).toLocaleString("en-IN");

// ─── Filter constants ─────────────────────────────────────────────────────────

const CASTE_FILTERS = [
  { label: "All Castes", keys: ["SC", "ST", "OBC","Muslim", "Brahmins", "Non-Brahmins", "Other"] },
  { label: "OBC", keys: ["OBC"] },
  { label: "SC/ST", keys: ["SC", "ST"] },
  { label: "General", keys: ["Brahmins", "Non-Brahmins", "Other"] },
  { label: "Muslim", keys: ["Muslim"] },
];

const RELIGION_FILTERS = ["All Religions", "Hindu", "Muslim", "Sikh"];
const GENDER_FILTERS = ["All", "Male", "Female"];
const AGE_BUCKETS = ["18-24", "25-34", "35-44", "45-54", "55-64", "65+"];

// ─── Small reusable components ────────────────────────────────────────────────

const MiniStat = ({ label, value, raw }) => (
  <div className="card-wrapper">
    <div className="election-mini-value">
      {raw ? value || 0 : formatIndianNumber(value)}
    </div>
    <div className="election-mini-label">{label}</div>
  </div>
);

const BottomStat = ({ label, value, sub, green, red }) => (
  <div className="election-stat">
    <div className="election-kicker">{label}</div>
    <div className="election-stat-value">
      {typeof value === "string" ? value : formatIndianNumber(value)}
    </div>
    <div
      className="election-stat-sub"
      style={{ color: green ? "#37d67a" : red ? "#ff4d4f" : undefined }}
    >
      {sub}
    </div>
  </div>
);

const TopFilters = ({
  data,
  filters,
  parties,
  onRegionChange,
  onDistrictChange,
  onConstituencyChange,
  onPartyChange,
}) => (
  <div className="election-dashboard-filters card-wrapper">
    <Select
      allowClear
      showSearch
      placeholder="Region"
      value={filters.region || undefined}
      onChange={onRegionChange}
      optionFilterProp="label"
      options={(data?.filters?.regions || []).map((r) => ({ value: r, label: r }))}
      getPopupContainer={(trigger) => trigger.parentNode}
    />
    <Select
      allowClear
      showSearch
      placeholder="District"
      value={filters.district || undefined}
      onChange={onDistrictChange}
      optionFilterProp="label"
      options={(data?.filters?.districts || []).map((d) => ({ value: d, label: d }))}
      getPopupContainer={(trigger) => trigger.parentNode}
    />
    <Select
      allowClear
      showSearch
      placeholder="Constituency"
      value={filters.constituency || undefined}
      onChange={onConstituencyChange}
      optionFilterProp="label"
      options={(data?.filters?.constituencies || []).map((c) => ({ value: c, label: c }))}
      getPopupContainer={(trigger) => trigger.parentNode}
    />
    <Select
      showSearch
      placeholder="Party"
      value={filters.party}
      onChange={onPartyChange}
      optionFilterProp="label"
      options={(parties.length ? parties : ["BJP"]).map((p) => ({ value: p, label: p }))}
      getPopupContainer={(trigger) => trigger.parentNode}
    />
  </div>
);

// ─── Left Panel ───────────────────────────────────────────────────────────────

const LeftPanel = ({
  data,
  filters,
  isLoading,
  onDistrictChange,
  onConstituencyChange,
  onPartyChange,
  parties,
  overview,
  community,
  religion,
  age,
  activeCaste,
  setActiveCaste,
  activeReligion,
  setActiveReligion,
  activeGender,
  setActiveGender,
  activeAge,
  setActiveAge,
  casteNumber,
  religionNumber,
  genderNumber,
  genderLabel,
}) => (
  <div className="election-dashboard-left card-wrapper">
    {isLoading ? (
      <div className="election-section">
        <Spin />
      </div>
    ) : (
      <>
        {/* Electoral Distribution */}
        <div className="election-section">
          <div className="election-section-title">Electoral Distribution</div>
          <div className="election-mini-grid">
            <MiniStat label="Total voters" value={overview.totalVoters} />
            <MiniStat label="Active voters" value={overview.activeVoters} />
            <MiniStat label="Inactive voters" value={overview.inactiveVoters} />
            <MiniStat label="Average age" value={overview.averageAge} raw />
            <MiniStat label="SIR Deduction" value={data?.panels?.electionResult?.sirDeduction} />
            <MiniStat label="SIR No. of Booth" value={data?.panels?.electionResult?.sirNumberOfBooths} />
          </div>
        </div>

        {/* Demographics */}
        <div className="election-section">
          <div className="election-section-title">Demographics</div>
          <div className="election-chip-row">
            {CASTE_FILTERS.map((item) => (
              <button
                key={item.label}
                className={`election-chip ${activeCaste === item.label ? "active" : ""}`}
                onClick={() => setActiveCaste(item.label)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="flex flex-col justify-center items-center m-2">
            <div className="election-stat-value !text-[22px]">
              {formatIndianNumber(casteNumber)}
            </div>
            <div className="election-mini-label !text-[#000]">
              Total voters in {activeCaste}
            </div>
          </div>

          {/* <div className="election-chip-row mt-4">
            {RELIGION_FILTERS.map((item) => (
              <button
                key={item}
                className={`election-chip ${activeReligion === item ? "active" : ""}`}
                onClick={() => setActiveReligion(item)}
              >
                {item}
              </button>
            ))}
          </div>
          <div className="flex flex-col justify-center items-center m-2">
            <div className="election-stat-value !text-[22px]">
              {formatIndianNumber(religionNumber)}
            </div>
            <div className="election-mini-label !text-[#000]">
              Total {activeReligion} voters
            </div>
          </div> */}
        </div>

        {/* Gender & Age */}
        <div className="election-section">
          <div className="election-section-title">Gender & Age</div>
          <div className="election-chip-row">
            {GENDER_FILTERS.map((item) => (
              <button
                key={item}
                className={`election-chip ${activeGender === item ? "active" : ""}`}
                onClick={() => setActiveGender(item)}
              >
                {item}
              </button>
            ))}
          </div>
          <div className="flex flex-col justify-center items-center m-2">
            <div className="election-stat-value !text-[22px]">{genderLabel}</div>
            <div className="election-mini-label !text-[#000]">
              Total {activeGender} voters
            </div>
          </div>

          <div className="election-chip-row mt-4">
            {AGE_BUCKETS.map((item) => (
              <button
                key={item}
                className={`election-chip ${activeAge === item ? "active" : ""}`}
                onClick={() => setActiveAge(item)}
              >
                {item}
              </button>
            ))}
          </div>
          <div className="flex flex-col justify-center items-center m-2">
            <div className="election-stat-value !text-[20px]">
              {formatIndianNumber(age[activeAge])}
            </div>
            <div className="election-mini-label !text-[#000]">{activeAge} voters</div>
          </div>
        </div>
      </>
    )}
  </div>
);

// ─── Main Dashboard ───────────────────────────────────────────────────────────

const ElectionModeDashboard = ({
  electionModeId,
  selectedConstituencyFromMap,
  mapAction,
  onLocationSelect,
  onDashboardMapChange,
  electionModeType,
  urbanRural,
}) => {
  const queryClient = useQueryClient();
  const axiosInstance = useAxiosInstance();
  const [showRightPanel, setShowRightPanel] = useState(true);
  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [showMlaPanel, setShowMlaPanel] = useState(false);
  const [showNewsPanel, setShowNewsPanel] = useState(false);
  const [activeRightTab, setActiveRightTab] = useState("Electoral");
  const [quickNoteRequest, setQuickNoteRequest] = useState(null);
  const [quickReportRequest, setQuickReportRequest] = useState(null);
  const [filters, setFilters] = useState({
    region: "",
    district: "",
    constituency: "",
    party: "BJP",
  });
  const [showLogoForm, setShowLogoForm] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [activeCaste, setActiveCaste] = useState(CASTE_FILTERS[0].label);
  const [activeReligion, setActiveReligion] = useState(RELIGION_FILTERS[0]);
  const [activeGender, setActiveGender] = useState(GENDER_FILTERS[0]);
  const [activeAge, setActiveAge] = useState(AGE_BUCKETS[0]);
  const [expandedPanels, setExpandedPanels] = useState({
    electionResult: true,
    favourability: false,
    community: false,
    religion: false,
    locations: false,
  });

  const togglePanel = (key) =>
    setExpandedPanels((prev) => ({ ...prev, [key]: !prev[key] }));

  // Sync constituency from map click
  useEffect(() => {
    if (!selectedConstituencyFromMap) return;
    setFilters((prev) => ({
      ...prev,
      region: "",
      district: "",
      constituency: selectedConstituencyFromMap,
    }));
  }, [selectedConstituencyFromMap]);

  useEffect(() => {
    if (!mapAction?.id) return;
    if (mapAction.type === "note") {
      setShowNotesPanel(true);
      setShowRightPanel(false);
      setQuickNoteRequest({
        id: mapAction.id,
        preset: {
          party: filters.party,
          ...(mapAction.scope === "district"
            ? { scope: "district", district: mapAction.district }
            : { scope: "constituency", constituency: mapAction.constituency }),
        },
      });
    }
    if (mapAction.type === "report") {
      setShowNotesPanel(false);
      setShowRightPanel(true);
      setShowMlaPanel(false);
      setActiveRightTab("Reports");
      setQuickReportRequest({ id: mapAction.id });
    }
  }, [mapAction?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Data fetch
  const { data, isLoading } = useQuery(
    ["electionModeDashboard", electionModeId, filters],
    async () => {
      const res = await axiosInstance.get(
        `/api/electionMode/dashboard/${electionModeId}`,
        {
          params: {
            district: filters.district || undefined,
            region: filters.region || undefined,
            constituency: filters.constituency || undefined,
            party: filters.party || "BJP",
          },
        }
      );
      return res.data?.data;
    },
    { enabled: !!electionModeId, refetchOnWindowFocus: false, retry: 1 }
  );

  // Propagate map changes up
  useEffect(() => {
    if (!data) return;
    onDashboardMapChange?.({
      region: filters.region,
      district: filters.district,
      constituency: filters.constituency,
      selectedConstituencies: data.map?.selectedConstituencies || [],
      // polygonColors: data.map?.polygonColors || [],
      // constituencySummaries: data.map?.constituencySummaries || {},
    });
  }, [data, filters.region, filters.district, filters.constituency, onDashboardMapChange]);

  // Parties list & auto-select
  const parties = useMemo(() => data?.filters?.parties || [], [data]);
  useEffect(() => {
    if (!parties.length || parties.includes(filters.party)) return;
    setFilters((prev) => ({
      ...prev,
      party: parties.includes("BJP") ? "BJP" : parties[0],
    }));
  }, [parties, filters.party]);

  // Derived data
  const overview = useMemo(() => data?.electoralDistribution || {}, [data]);
  const community = useMemo(() => overview.communityDistribution || {}, [overview]);
  const religion = useMemo(() => overview.religionDistribution || {}, [overview]);
  const age = useMemo(() => overview.ageDistribution || {}, [overview]);
  const bottomStats = data?.bottomStats || {};
  const panels = data?.panels || {};

  const communityChart = Object.entries(panels.communityDistribution || {}).map(
    ([name, value]) => ({ name, value })
  );
  const religionChart = Object.entries(panels.religionDistribution || {}).map(
    ([name, value]) => ({ name, value })
  );

  // Demographic calculations
  const casteNumber = useMemo(() => {
    const selected = CASTE_FILTERS.find((item) => item.label === activeCaste);
    return (selected?.keys || []).reduce(
      (total, key) => total + (Number(community[key]) || 0),
      0
    );
  }, [activeCaste, community]);

  const religionNumber =
    activeReligion === "All Religions"
      ? Object.values(religion).reduce((t, v) => t + (Number(v) || 0), 0)
      : Number(religion[activeReligion]) || 0;

  const genderTotal = (overview.maleVoters || 0) + (overview.femaleVoters || 0);
  const genderNumber =
    activeGender === "Male"
      ? overview.maleVoters || 0
      : activeGender === "Female"
      ? overview.femaleVoters || 0
      : genderTotal;

  const genderLabel =
    activeGender === "All"
      ? formatIndianNumber(genderNumber)
      : `${formatIndianNumber(genderNumber)} (${Math.round(
          genderTotal ? (genderNumber / genderTotal) * 100 : 0
        )}%)`;

  const uploadLogoMutation = useMutation(
    async () => {
      if (!logoFile) {
        throw new Error("No file selected");
      }

      const formData = new FormData();
      formData.append("logo", logoFile);

      return axiosInstance.post(
        `api/electionMode/${electionModeId}/logo`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    },
    {
      onSuccess: () => {
        toast.success("Logo uploaded successfully");
        setLogoFile(null);

        queryClient.invalidateQueries([
          "electionModeDashboard",
          electionModeId,
        ]);
        queryClient.invalidateQueries(["election-mode-logo", electionModeId]);
      },
      onError: () => {
        toast.error("Unable to upload logo");
      },
    }
  );

  const logoQuery = useQuery(
    ["election-mode-logo", electionModeId],
    async () => {
      const response = await axiosInstance.get(`api/electionMode/${electionModeId}/logo`);
      return response.data?.data;
    },
    { enabled: Boolean(electionModeId), refetchOnWindowFocus: false, retry: 1 }
  );

  const deleteLogoMutation = useMutation(
    () => axiosInstance.delete(`api/electionMode/${electionModeId}/logo`),
    {
      onSuccess: () => {
        toast.success("Logo deleted successfully");
        queryClient.invalidateQueries(["election-mode-logo", electionModeId]);
      },
      onError: () => toast.error("Unable to delete logo"),
    }
  );

  // Handlers
  const handleRegionChange = (value) => {
    setFilters((prev) => ({
      ...prev,
      region: value || "",
      district: "",
      constituency: "",
    }));
    onLocationSelect?.(null);
  };
  const handleDistrictChange = (value) => {
    setFilters((prev) => ({
      ...prev,
      region: "",
      district: value || "",
      constituency: "",
    }));
    onLocationSelect?.(null);
  };
  const handleConstituencyChange = (value) => {
    setFilters((prev) => ({ ...prev, constituency: value || "" }));
    onLocationSelect?.(value || null);
  };

  return (
    <div className="election-dashboard">

      <div className="absolute top-[1%] left-[10%] flex flex-col gap-3 z-[9999]">
        <div className="card-wrapper flex flex-col gap-3">
          <Button
            style={{ pointerEvents: "auto" }}
            type="primary"
            className="shadow-lg flex items-center justify-center text-black"
            icon={<UploadIcon size={16} />}
            onClick={() => setShowLogoForm((prev) => !prev)}
          >
            {logoQuery.data ? "Edit Logo" : "Add Logo"}
          </Button>

          {showLogoForm && (
            <div className="flex flex-col gap-3 mt-2">
              <Upload
                beforeUpload={(file) => {
                  const isImage = file.type.startsWith("image/");

                  if (!isImage) {
                    toast.error("Only image files are allowed");
                    return Upload.LIST_IGNORE;
                  }

                  setLogoFile(file);
                  return false;
                }}
                maxCount={1}
                accept="image/*"
                showUploadList
              >
                <Button icon={<UploadIcon size={16} />}>
                  Upload Image
                </Button>
              </Upload>

              <div className="flex gap-2">
                <Button
                  type="primary"
                  loading={uploadLogoMutation.isLoading}
                  onClick={() => uploadLogoMutation.mutate()}
                  disabled={!logoFile}
                >
                  Save
                </Button>

                <Button
                  onClick={() => {
                    setShowLogoForm(false);
                    setLogoFile(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {logoQuery.data?.publicUrl && (
            <>
              <img
                src={logoQuery.data.publicUrl}
                alt="Election logo"
                className="h-10 max-w-[130px] object-contain"
              />

              <Button
                danger
                size="small"
                loading={deleteLogoMutation.isLoading}
                onClick={() => deleteLogoMutation.mutate()}
              >
                Delete Logo
              </Button>
            </>
          )}
        </div>
      </div>

      <TopFilters
        data={data}
        filters={filters}
        parties={parties}
        onRegionChange={handleRegionChange}
        onDistrictChange={handleDistrictChange}
        onConstituencyChange={handleConstituencyChange}
        onPartyChange={(party) => setFilters((prev) => ({ ...prev, party }))}
      />

      {/* <div className="absolute top-[46%] right-4 flex flex-col gap-2">
          <Button
            style={{ pointerEvents: "auto" }}
            type="default"
            className="bg-white text-black border-none rounded-xl shadow-lg transition-all duration-300 ease-in-out hover:scale-105 flex items-center justify-center h-12 z-[9999]"
            onClick={() => {
              setShowNotesPanel(false);
              setShowRightPanel(!showRightPanel);
            }}
            icon={showRightPanel ? <AlignLeft size={25} /> : <AlignRight size={25} />}
          >
            {!showRightPanel ? `Open ${activeRightTab}` : ""}
          </Button>
          <Button
            style={{ pointerEvents: "auto" }}
            type="default"
            className="bg-white text-black border-none rounded-xl shadow-lg transition-all duration-300 ease-in-out hover:scale-105 flex items-center justify-center h-12 z-[9999]"
            icon={<StickyNote size={22} />}
            onClick={() => {
              setShowNotesPanel(!showNotesPanel);
              setShowRightPanel(false);
            }}
          >
            Notes
          </Button>
      </div> */}
      
      <div className="absolute top-[46%] right-4 flex flex-col gap-3 z-[9999]">
        <Tooltip
          placement="left"
          title={showRightPanel ? `Close ${activeRightTab}` : `Open ${activeRightTab}`}
          overlayStyle={{ zIndex: 10000 }}
          getPopupContainer={(trigger) => trigger.parentNode}
        >
          <Button
            style={{ pointerEvents: "auto" }}
            type="default"
            shape="circle"
            size="large"
            className="bg-white text-black border-none shadow-lg transition-all duration-300 ease-in-out hover:scale-110 flex items-center justify-center"
            onClick={() => {
              setShowNotesPanel(false);
              setShowMlaPanel(false);
              setShowNewsPanel(false);
              setShowRightPanel(!showRightPanel);
            }}
            icon={
              showRightPanel ? (
                <ChevronRight size={22} />
              ) : (
                <ChevronLeft size={22} />
              )
            }
          />
        </Tooltip>

        <Tooltip placement="left" title={showNotesPanel ? "Close Notes" : "Open Notes"} overlayStyle={{ zIndex: 10000 }} getPopupContainer={(trigger) => trigger.parentNode}>
          <Button
            style={{ pointerEvents: "auto" }}
            type="default"
            shape="circle"
            size="large"
            className="bg-white text-black border-none shadow-lg transition-all duration-300 ease-in-out hover:scale-110 flex items-center justify-center"
            icon={<StickyNote size={20} />}
            onClick={() => {
              setShowNotesPanel(!showNotesPanel);
              setShowMlaPanel(false);
              setShowNewsPanel(false);
              setShowRightPanel(false);
            }}
          />
        </Tooltip>

        <Tooltip
          placement="left"
          title={showMlaPanel ? "Close MLA Affidavit" : "Open MLA Affidavit"}
          overlayStyle={{ zIndex: 10000 }}
          getPopupContainer={(trigger) => trigger.parentNode}
        >
          <Button
            style={{ pointerEvents: "auto" }}
            type="default"
            shape="circle"
            size="large"
            className="bg-white text-black border-none shadow-lg transition-all duration-300 ease-in-out hover:scale-110 flex items-center justify-center"
            icon={<FileSpreadsheet size={20} />}
            onClick={() => {
              setShowMlaPanel(!showMlaPanel);
              setShowNotesPanel(false);
              setShowNewsPanel(false);
              setShowRightPanel(false);
            }}
          />
        </Tooltip>

        <Tooltip
          placement="left"
          title={showNewsPanel ? "Close News" : "Open News"}
          overlayStyle={{ zIndex: 10000 }}
          getPopupContainer={(trigger) => trigger.parentNode}
        >
          <Button
            style={{ pointerEvents: "auto" }}
            type="default"
            shape="circle"
            size="large"
            className="bg-white text-black border-none shadow-lg transition-all duration-300 ease-in-out hover:scale-110 flex items-center justify-center"
            icon={<Newspaper size={20} />}
            onClick={() => {
              setShowNewsPanel(!showNewsPanel);
              setShowMlaPanel(false);
              setShowNotesPanel(false);
              setShowRightPanel(false);
            }}
          />
        </Tooltip>
      </div>

      {/* Left */}
      <LeftPanel
        data={data}
        filters={filters}
        isLoading={isLoading}
        onDistrictChange={handleDistrictChange}
        onConstituencyChange={handleConstituencyChange}
        onPartyChange={(party) => setFilters((prev) => ({ ...prev, party }))}
        parties={parties}
        overview={overview}
        community={community}
        religion={religion}
        age={age}
        activeCaste={activeCaste}
        setActiveCaste={setActiveCaste}
        activeReligion={activeReligion}
        setActiveReligion={setActiveReligion}
        activeGender={activeGender}
        setActiveGender={setActiveGender}
        activeAge={activeAge}
        setActiveAge={setActiveAge}
        casteNumber={casteNumber}
        religionNumber={religionNumber}
        genderNumber={genderNumber}
        genderLabel={genderLabel}
      />

      {/* Right — now a standalone component with Electoral / Reports tabs */}
       <AnimatePresence>
        { showRightPanel && 
          <motion.div 
              key="dashboard"
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 100 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              // className="absolute top-[12%] right-2 !p-2 card-wrapper !rounded-xl flex flex-col items-center w-[500px] max-h-[80vh] !overflow-y-auto scroll-smooth"
            >
            <RightPanel
              electionModeId={electionModeId}
              filters={filters}
              districts={data?.filters?.districts || []}
              constituencyOptions={data?.filters?.constituencies || []}
              electionModeType={electionModeType}
              urbanRural={urbanRural}
              parties={parties}
              panels={panels}
              communityChart={communityChart}
              religionChart={religionChart}
              expandedPanels={expandedPanels}
              onToggle={togglePanel}
              activeTab={activeRightTab}
              onActiveTabChange={setActiveRightTab}
              quickReportRequest={quickReportRequest}
            />
          </motion.div>
        }
      </AnimatePresence>

      <AnimatePresence>
        {showNotesPanel && (
          <motion.div
            key="notes"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
          >
            <NotesPanel
              electionModeId={electionModeId}
              filters={filters}
              districts={data?.filters?.districts || []}
              constituencies={data?.filters?.constituencies || []}
              parties={parties}
              quickNoteRequest={quickNoteRequest}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showMlaPanel && (
          <motion.div
            key="mla-affidavit"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
          >
            <div className="election-dashboard-right card-wrapper">
              <MlaAffidavitPanel
                electionModeId={electionModeId}
                filters={filters}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showNewsPanel && (
          <motion.div
            key="news-panel"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
          >
            <div className="election-dashboard-right card-wrapper">
              <NewsPanelAdmin electionModeId={electionModeId} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom */}
      <div className="election-dashboard-bottom card-wrapper">
        <BottomStat label="Total Seats" value={bottomStats.totalSeats} sub="State assembly" />
        <BottomStat
          label="Projected Wins"
          value={bottomStats.projectedWins}
          sub={`${filters.party} favourable`}
          green
        />
        <BottomStat
          label="Avg Vote Share"
          value={`${bottomStats.avgVoteShare || 0}%`}
          sub={filters.party}
        />
        <BottomStat
          label="Margin At Risk"
          value={bottomStats.marginAtRisk}
          sub="Difficult seats"
          red
        />
      </div>
    </div>
  );
};

export default ElectionModeDashboard;
